import { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

const FORM_URL = 'https://readdy.ai/api/form/d72gqa4juufb4jdbl400';

interface BesparingCalculatorProps {
  isOpen: boolean;
  onClose: () => void;
}

type Step = 'form' | 'result' | 'confirm';

export default function BesparingCalculator({ isOpen, onClose }: BesparingCalculatorProps) {
  const [step, setStep] = useState<Step>('form');
  const [productNaam, setProductNaam] = useState('');
  const [inkoopprijs, setInkoopprijs] = useState('');
  const [aantalStuds, setAantalStuds] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);

  const priceValue = parseFloat(inkoopprijs.replace(',', '.')) || 0;
  const aantalValue = parseInt(aantalStuds, 10) || 0;
  const totaalprijs = priceValue * aantalValue;
  const minBesparing = totaalprijs * 0.30;
  const maxBesparing = totaalprijs * 0.80;

  const formatEuro = (val: number) => {
    if (val === 0) return '0,00';
    if (val < 0.01) return val.toLocaleString('nl-NL', { minimumFractionDigits: 5, maximumFractionDigits: 5 });
    if (val < 1) return val.toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 4 });
    return val.toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const handleCalculate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!productNaam.trim() || priceValue <= 0 || aantalValue <= 0) {
      setError('Vul een geldig productnaam, prijs en aantal stuks in.');
      return;
    }
    setError('');
    setSubmitting(true);

    const params = new URLSearchParams({
      product_naam: productNaam.trim(),
      inkoopprijs_nederland: `€${inkoopprijs}`,
      aantal_stuks: aantalValue.toString(),
      totaalprijs: `€${formatEuro(totaalprijs)}`,
      min_besparing: `€${formatEuro(minBesparing)}`,
      max_besparing: `€${formatEuro(maxBesparing)}`,
      berekend_op: new Date().toLocaleDateString('nl-NL'),
    });

    try {
      await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
    } catch {
      // submission failure shouldn't block UX
    } finally {
      setSubmitting(false);
      setStep('result');
    }
  };

  const handleOfferteClick = () => {
    onClose();
    setTimeout(() => {
      navigate('/offerte', {
        state: {
          productNaam: productNaam,
          aantalStuds: aantalStuds,
          totaalprijs: `€${formatEuro(totaalprijs)}`,
        },
      });
    }, 200);
  };

  const handleClose = () => {
    onClose();
    setTimeout(() => {
      setStep('form');
      setProductNaam('');
      setInkoopprijs('');
      setAantalStuds('');
      setError('');
    }, 300);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            className="fixed inset-0 bg-black/55 backdrop-blur-sm z-[9990]"
          />

          {/* Modal */}
          <div className="fixed inset-0 z-[9991] flex items-center justify-center px-4 pointer-events-none">
            <motion.div
              key={step}
              initial={{ opacity: 0, scale: 0.93, y: 24 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.93, y: 24 }}
              transition={{ type: 'spring', stiffness: 340, damping: 30 }}
              className="pointer-events-auto bg-white rounded-2xl w-full max-w-md relative overflow-hidden"
            >
              {/* Header strip */}
              <div className={`px-6 pt-6 pb-5 ${step === 'confirm' ? 'bg-gradient-to-r from-[#1a3a1a] to-[#2a5a2a]' : 'bg-gradient-to-r from-[#0A1628] to-[#1a2f52]'}`}>
                <button
                  onClick={handleClose}
                  className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full text-white/40 hover:text-white hover:bg-white/10 transition-all cursor-pointer"
                >
                  <i className="ri-close-line text-base" />
                </button>
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0 ${step === 'confirm' ? 'bg-green-400/25' : 'bg-[#3B9EE8]/25'}`}>
                    {step === 'form' && <i className="ri-calculator-line text-[#3B9EE8] text-lg" />}
                    {step === 'result' && <i className="ri-money-euro-circle-line text-[#3B9EE8] text-lg" />}
                    {step === 'confirm' && <i className="ri-checkbox-circle-line text-green-400 text-lg" />}
                  </div>
                  <div>
                    {step === 'form' && (
                      <>
                        <h2 className="text-white font-700 text-base leading-tight">Bereken je besparing</h2>
                        <p className="text-white/50 text-xs mt-0.5">Ontdek hoeveel je kunt besparen via China</p>
                      </>
                    )}
                    {step === 'result' && (
                      <>
                        <h2 className="text-white font-700 text-base leading-tight">Jouw besparingsindicatie</h2>
                        <p className="text-white/50 text-xs mt-0.5">Op basis van {aantalValue.toLocaleString('nl-NL')} stuks {productNaam}</p>
                      </>
                    )}
                    {step === 'confirm' && (
                      <>
                        <h2 className="text-white font-700 text-base leading-tight">Wil je een exacte offerte?</h2>
                        <p className="text-white/50 text-xs mt-0.5">Gratis begeleiding &amp; advies inbegrepen</p>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Body */}
              <div className="px-6 py-6">
                {/* ── STEP 1: FORM ── */}
                {step === 'form' && (
                  <form
                    data-readdy-form
                    id="besparing-calculator-form"
                    onSubmit={handleCalculate}
                    className="flex flex-col gap-4"
                  >
                    {/* Product naam */}
                    <div className="flex flex-col gap-1.5">
                      <label className="text-xs font-600 text-[#0A1628]/60 uppercase tracking-wide">
                        Naam van het product
                      </label>
                      <input
                        ref={inputRef}
                        type="text"
                        name="product_naam"
                        required
                        placeholder="bv. Katoenen tassen, Powerbanks…"
                        value={productNaam}
                        onChange={(e) => setProductNaam(e.target.value)}
                        className="w-full border border-[#0A1628]/12 rounded-lg px-4 py-3 text-sm text-[#0A1628] placeholder-[#0A1628]/30 focus:outline-none focus:border-[#3B9EE8] focus:ring-2 focus:ring-[#3B9EE8]/15 transition-all"
                      />
                    </div>

                    {/* Inkoopprijs */}
                    <div className="flex flex-col gap-1.5">
                      <label className="text-xs font-600 text-[#0A1628]/60 uppercase tracking-wide">
                        Huidige inkoopprijs per stuk in Nederland (€)
                      </label>
                      <div className="relative">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm font-600 text-[#0A1628]/40">€</span>
                        <input
                          type="number"
                          name="inkoopprijs_nederland"
                          required
                          min="0.00001"
                          step="any"
                          placeholder="0.00"
                          value={inkoopprijs}
                          onChange={(e) => setInkoopprijs(e.target.value)}
                          className="w-full border border-[#0A1628]/12 rounded-lg pl-8 pr-4 py-3 text-sm text-[#0A1628] placeholder-[#0A1628]/30 focus:outline-none focus:border-[#3B9EE8] focus:ring-2 focus:ring-[#3B9EE8]/15 transition-all"
                        />
                      </div>
                      <p className="text-[#0A1628]/35 text-xs">Vul de prijs per stuk in die je nu betaalt bij Nederlandse leveranciers.</p>
                    </div>

                    {/* Aantal stuks */}
                    <div className="flex flex-col gap-1.5">
                      <label className="text-xs font-600 text-[#0A1628]/60 uppercase tracking-wide">
                        Hoeveel stuks wilt u inkopen? <span className="text-red-400">*</span>
                      </label>
                      <div className="relative">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#0A1628]/40">
                          <i className="ri-stack-line text-sm" />
                        </span>
                        <input
                          type="number"
                          name="aantal_stuks"
                          required
                          min="1"
                          step="1"
                          placeholder="bv. 500"
                          value={aantalStuds}
                          onChange={(e) => setAantalStuds(e.target.value)}
                          className="w-full border border-[#0A1628]/12 rounded-lg pl-9 pr-4 py-3 text-sm text-[#0A1628] placeholder-[#0A1628]/30 focus:outline-none focus:border-[#3B9EE8] focus:ring-2 focus:ring-[#3B9EE8]/15 transition-all"
                        />
                      </div>
                      <p className="text-[#0A1628]/35 text-xs">Voer het gewenste bestelvolume in stuks in.</p>
                    </div>

                    {/* Live preview */}
                    {priceValue > 0 && aantalValue > 0 && (
                      <div className="bg-[#f7faff] border border-[#3B9EE8]/15 rounded-lg px-4 py-3 flex items-center justify-between">
                        <span className="text-xs text-[#0A1628]/50">Totale orderwaarde:</span>
                        <span className="text-sm font-700 text-[#0A1628]">€{formatEuro(totaalprijs)}</span>
                      </div>
                    )}

                    {error && (
                      <p className="text-red-500 text-xs bg-red-50 px-3 py-2 rounded-lg">{error}</p>
                    )}

                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full bg-[#3B9EE8] hover:bg-[#2D85C5] disabled:opacity-60 text-white font-700 text-sm py-3 rounded-lg transition-all duration-200 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                    >
                      {submitting ? (
                        <>
                          <i className="ri-loader-4-line animate-spin text-base" />
                          Berekenen…
                        </>
                      ) : (
                        <>
                          <i className="ri-calculator-line text-base" />
                          Bereken mijn besparing
                        </>
                      )}
                    </button>

                    <p className="text-center text-[#0A1628]/25 text-xs">
                      Vrijblijvend &bull; Geen verplichtingen &bull; 100% gratis
                    </p>
                  </form>
                )}

                {/* ── STEP 2: RESULT ── */}
                {step === 'result' && (
                  <div className="flex flex-col gap-5">
                    {/* Totaalprijs */}
                    <div className="bg-[#f7faff] border border-[#3B9EE8]/20 rounded-xl px-4 py-3 flex items-center justify-between">
                      <div>
                        <p className="text-[#0A1628]/45 text-xs font-500">Totale orderwaarde</p>
                        <p className="text-[#0A1628] font-700 text-base">€{formatEuro(totaalprijs)}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-[#0A1628]/45 text-xs font-500">{aantalValue.toLocaleString('nl-NL')} stuks</p>
                        <p className="text-[#0A1628]/60 text-xs">à €{formatEuro(priceValue)} p.s.</p>
                      </div>
                    </div>

                    {/* Besparing result card */}
                    <div className="bg-gradient-to-br from-[#f0faf0] to-[#e8f5e9] border border-green-200/60 rounded-xl p-5 text-center">
                      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-green-500/15 mx-auto mb-3">
                        <i className="ri-money-euro-circle-line text-green-600 text-xl" />
                      </div>
                      <p className="text-[#0A1628]/55 text-xs font-500 mb-1 uppercase tracking-wide">
                        Geschatte besparing via DUC
                      </p>
                      <div className="flex items-center justify-center gap-2 my-2">
                        <span className="text-3xl font-800 text-green-700">
                          €{formatEuro(minBesparing)}
                        </span>
                        <span className="text-[#0A1628]/30 font-500 text-lg">—</span>
                        <span className="text-3xl font-800 text-green-700">
                          €{formatEuro(maxBesparing)}
                        </span>
                      </div>
                      <p className="text-[#0A1628]/40 text-xs">
                        30–80% besparing op je totale orderwaarde van €{formatEuro(totaalprijs)}
                      </p>
                    </div>

                    {/* CTA naar confirm */}
                    <button
                      onClick={() => setStep('confirm')}
                      className="w-full bg-[#3B9EE8] hover:bg-[#2D85C5] text-white font-700 text-sm py-3.5 rounded-lg transition-all duration-200 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                    >
                      <i className="ri-file-list-3-line text-base" />
                      Ontvang mijn exacte offerte →
                    </button>

                    <button
                      onClick={() => setStep('form')}
                      className="text-center text-[#0A1628]/35 hover:text-[#3B9EE8] text-xs cursor-pointer transition-colors"
                    >
                      ← Ander product berekenen
                    </button>
                  </div>
                )}

                {/* ── STEP 3: CONFIRM ── */}
                {step === 'confirm' && (
                  <div className="flex flex-col gap-5">
                    {/* Indicatie disclaimer */}
                    <div className="bg-amber-50 border border-amber-200/60 rounded-xl px-4 py-3 flex items-start gap-3">
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-information-line text-amber-500 text-base" />
                      </div>
                      <p className="text-amber-800 text-xs leading-relaxed">
                        <strong>Dit is puur een indicatie</strong> — de werkelijke besparing kan hoger of lager uitvallen afhankelijk van het product, volume en specificaties.
                      </p>
                    </div>

                    {/* Besparing recap klein */}
                    <div className="bg-[#f7faff] border border-[#3B9EE8]/15 rounded-xl px-4 py-3 text-center">
                      <p className="text-[#0A1628]/50 text-xs mb-1">Indicatieve besparing op <strong className="text-[#0A1628]/70">{productNaam}</strong></p>
                      <p className="text-[#0A1628] font-800 text-lg">
                        €{formatEuro(minBesparing)} — €{formatEuro(maxBesparing)}
                      </p>
                      <p className="text-[#0A1628]/35 text-xs mt-0.5">{aantalValue.toLocaleString('nl-NL')} stuks &bull; €{formatEuro(totaalprijs)} totaal</p>
                    </div>

                    {/* Vraag */}
                    <div className="text-center">
                      <p className="text-[#0A1628] font-700 text-base leading-snug mb-1">
                        Wil je een exacte offerte ontvangen?
                      </p>
                      <p className="text-[#0A1628]/50 text-sm leading-relaxed">
                        Met gratis begeleiding en persoonlijk advies van ons team — volledig vrijblijvend.
                      </p>
                    </div>

                    {/* Ja / Nee knoppen */}
                    <div className="flex flex-col gap-3">
                      <button
                        onClick={handleOfferteClick}
                        className="w-full bg-green-600 hover:bg-green-700 text-white font-700 text-sm py-3.5 rounded-lg transition-all duration-200 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                      >
                        <i className="ri-check-line text-base" />
                        Ja, ik wil een gratis offerte aanvragen!
                      </button>

                      <button
                        onClick={handleClose}
                        className="w-full border border-[#0A1628]/12 hover:border-[#0A1628]/25 text-[#0A1628]/55 hover:text-[#0A1628]/80 font-600 text-sm py-3 rounded-lg transition-all duration-200 cursor-pointer whitespace-nowrap"
                      >
                        Nee, bedankt — ik kijk nog even verder
                      </button>
                    </div>

                    <p className="text-center text-[#0A1628]/25 text-xs">
                      Geen verplichtingen &bull; Reageren binnen 24 uur &bull; 100% gratis
                    </p>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}
